import { CapTableRail, HoldingRes } from '@dealstack/shared/lib/generated';
import { HoldingCustomSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingCustomSettings';
import { CreateHoldingEventDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/CreateHoldingEventDrawer/CreateHoldingEventDrawer.tsx';
import { DeleteHoldingEventDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/DeleteHoldingEventDrawer/DeleteHoldingEventDrawer.tsx';
import { EditHoldingEventDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/EditHoldingEventDrawer/EditHoldingEventDrawer.tsx';
import {
	RegularViewTableItemData,
	useRegularViewTableController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/useRegularViewTableController.ts';
import { HoldingsType } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable';
import { getTableSelection } from '@dealstack/frontend/shared/components/Table/MantineTable/table-utils';
import { StorageKeyMap } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useTableColPreferences } from '@dealstack/frontend/shared/hooks/useTableColPreferences/useTableColPreferences.hook.ts';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { browserDateToMidnightUTC } from '@dealstack/frontend/shared/utils/utc.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Table } from '@tanstack/react-table';
import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	BulkAction,
	BulkActionGroup,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ActionsButton/ActionsButton.types';
import { BulkCreateHoldingEventDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/BulkCreateHoldingEventDrawer/BulkCreateHoldingEventDrawer';
import { BulkDeleteHoldingEventDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/DeleteHoldingEventDrawer/BulkDeleteHoldingEventDrawer';

interface Props {
	showSummarized: boolean;
	showPercentages: boolean;
	isLoading: boolean;
	viewByBlock: boolean;
	tableData: HoldingRes[];
	tableType: HoldingsType;
	refetchHoldingsList?: () => void;
	filterDate?: Date | null;
	colPreferencesKey: keyof StorageKeyMap;
	/**
	 * CAP-1976: the rail these rows were read on, carried into the drilldown. The details page has
	 * its own rail toggle and defaults to beneficial, so without this a click from the Legal view
	 * opened the beneficial position — a holder with legal-only units read as an empty holding.
	 */
	rail?: CapTableRail;
}

const createBulkActions = (
	table: Table<RegularViewTableItemData>,
	{
		isLoading,
		tCapManage,
		handleBulkCreateEvent,
		handleDeleteSelectedEvents,
	}: {
		isLoading: boolean;
		tCapManage: (key: string) => string;
		handleBulkCreateEvent: (table: Table<RegularViewTableItemData>) => void;
		handleDeleteSelectedEvents: (table: Table<RegularViewTableItemData>) => void;
	}
): BulkActionGroup<RegularViewTableItemData> => {
	const primary: BulkAction<RegularViewTableItemData>[] = [];
	const secondary: BulkAction<RegularViewTableItemData>[] = [];
	const special: BulkAction<RegularViewTableItemData>[] = [];

	primary.push({
		key: 'add-event',
		label: tCapManage('holdings.event.add'),
		disabled: isLoading,
		onExecute: () => handleBulkCreateEvent(table),
	});

	primary.push({
		key: 'remove-event',
		label: tCapManage('holdings.event.remove'),
		buttonLabel: tCapManage('holdings.event.remove'),
		disabled: isLoading,
		onExecute: () => handleDeleteSelectedEvents(table),
	});

	return { primary, secondary, special };
};

export const RegularViewTable = ({
	showSummarized,
	showPercentages,
	isLoading,
	tableData,
	tableType,
	refetchHoldingsList,
	viewByBlock,
	filterDate,
	colPreferencesKey,
	rail,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [showBulkCreateDrawer, setShowBulkCreateDrawer] = useState(false);
	const [showBulkDeleteDrawer, setShowBulkDeleteDrawer] = useState(false);
	const [bulkDeleteEventIds, setBulkDeleteEventIds] = useState<string[]>([]);
	const [rows, setRows] = useState<RegularViewTableItemData[]>([]);
	const tableInstanceRef = useRef<Table<RegularViewTableItemData> | null>(null);

	const resetTableSelection = useCallback(() => {
		if (tableInstanceRef.current) {
			tableInstanceRef.current.resetRowSelection();
		}
		setRows([]);
		setBulkDeleteEventIds([]);
	}, []);
	const location = useLocation();
	const navigate = useNavigate();
	const { columnVisibility, handleColumnVisibilityChange } = useTableColPreferences({
		storageKey: colPreferencesKey,
		defaultColumnVisibility: {
			heldFor: false,
			totalNominalValue: false,
			totalShareCapital: false,
			totalSharePremium: false,
			holderName: true,
			mipName: true,
			securityName: true,
			unitsAmount: true,
			totalCost: true,
			votesAmount: true,
			associatedBlocks: true,
			events: true,
		},
	});

	const {
		regularViewData,
		debtHoldings,
		columns,
		mantineTableDataOperations,
		showCreateHoldingsEventDrawer,
		setShowCreateHoldingsEventDrawer,
		showEditHoldingsEventDrawer,
		setShowEditHoldingsEventDrawer,
		showDeleteHoldingsEventDrawer,
		setShowDeleteHoldingsEventDrawer,
		setHoldingsEventEdit,
		holdingsEventEdit,
		holding,
		setHolding,
	} = useRegularViewTableController({
		showSummarized,
		showPercentages,
		tableData,
		tableType,
		viewByBlock,
	});

	const navigateToHoldingDetailsPage = (row: RegularViewTableItemData) => {
		const queryParams = new URLSearchParams({
			securityId: row.securityId,
			holderId: row.holderId,
			...(row.mipId ? { mipId: row.mipId } : {}),
			...(filterDate
				? { filterDate: browserDateToMidnightUTC(filterDate)?.split('T')[0] }
				: {}),
			...(rail ? { rail } : {}),
		});

		const holdingTypesPattern = Object.values(HoldingsType)
			.map((type) => `\\/${type}`)
			.join('|');

		const holdingRoutesPattern = new RegExp(`(${holdingTypesPattern}).*$`);
		const basePath = location.pathname.replace(holdingRoutesPattern, '');
		const fullPath = `${basePath}${PEOPLE_ROUTES.HOLDING_DETAILS}?${queryParams.toString()}`;

		return navigate(fullPath, {
			state: {
				navigationSource: tableType === HoldingsType.OwnershipTable,
			},
		});
	};

	const handleDeleteSelectedEvents = useCallback((table: Table<RegularViewTableItemData>) => {
		tableInstanceRef.current = table;
		const selectedRows = getTableSelection(table);
		const eventIds: string[] = [];

		selectedRows.forEach((row) => {
			const rowData = row.original;
			if (rowData.events && rowData.events.length > 0) {
				rowData.events.forEach((event) => {
					if (event.id) {
						eventIds.push(event.id);
					}
				});
			}
		});

		if (eventIds.length > 0) {
			setBulkDeleteEventIds(eventIds);
			setShowBulkDeleteDrawer(true);
		}
	}, []);

	const handleBulkCreateEvent = useCallback((table: Table<RegularViewTableItemData>) => {
		tableInstanceRef.current = table;
		const selectedRows = getTableSelection(table);

		const events: RegularViewTableItemData[] = [];

		selectedRows.forEach((row) => {
			const rowData = row.original as RegularViewTableItemData;

			if (rowData && rowData.holderId && rowData.securityId) {
				events.push(rowData);
			}
		});

		if (events.length > 0) {
			setRows(events);
			setShowBulkCreateDrawer(true);
		}
	}, []);

	return (
		<>
			<MantineTable<RegularViewTableItemData>
				columns={columns}
				data={regularViewData}
				isLoading={isLoading}
				dataOperations={mantineTableDataOperations}
				dataTestId={TEST_ELEMENT_IDS.CAP_OWNERSHIP_TABLE_BODY}
				enableRowSelection={true}
				enableRowActions={false}
				manualPagination={false}
				enablePagination={true}
				manualFiltering={false}
				manualSorting={false}
				enableStickyHeader={true}
				enableColumnResizing={true}
				pinnedColumnIds={['holderName']}
				getRowId={(row, i) => `${row.holderId}-${row.securityId}:${i}`}
				initialState={{
					columnVisibility,
				}}
				state={{
					columnVisibility,
				}}
				onColumnVisibilityChange={handleColumnVisibilityChange}
				renderBulkActionsMenu
				actions={(table) =>
					createBulkActions(table, {
						isLoading,
						tCapManage,
						handleBulkCreateEvent,
						handleDeleteSelectedEvents,
					})
				}
				customSettingsRenderer={(table) => (
					<HoldingCustomSettings<RegularViewTableItemData> table={table} />
				)}
				mantineTableBodyRowProps={({ row }) => ({
					id: row.id,
					onClick: () => navigateToHoldingDetailsPage(row.original),
					style: {
						cursor: 'pointer',
					},
				})}
			/>

			{holding && (
				<CreateHoldingEventDrawer
					holding={holding}
					opened={showCreateHoldingsEventDrawer}
					onClose={() => {
						setShowCreateHoldingsEventDrawer(false);
						setHolding(undefined);
					}}
					refreshSecuritiesList={() => refetchHoldingsList && refetchHoldingsList()}
					existingEvents={holdingsEventEdit}
					debtHoldings={debtHoldings}
				/>
			)}

			{holding && (
				<EditHoldingEventDrawer
					holding={holding}
					opened={showEditHoldingsEventDrawer}
					onClose={() => {
						setHoldingsEventEdit(undefined);
						setShowEditHoldingsEventDrawer(false);
						setHolding(undefined);
					}}
					holdingsEvent={holdingsEventEdit || []}
					debtHoldings={debtHoldings}
					refreshSecuritiesList={() => refetchHoldingsList && refetchHoldingsList()}
				/>
			)}

			{holding && (
				<DeleteHoldingEventDrawer
					holding={holding}
					opened={showDeleteHoldingsEventDrawer}
					onClose={() => {
						setShowDeleteHoldingsEventDrawer(false);
						setHolding(undefined);
					}}
					refreshSecuritiesList={() => refetchHoldingsList && refetchHoldingsList()}
					holdingsEvents={holdingsEventEdit || []}
				/>
			)}

			<BulkCreateHoldingEventDrawer
				selectedRows={rows}
				opened={showBulkCreateDrawer}
				onClose={() => {
					setShowBulkCreateDrawer(false);
				}}
				onSuccess={resetTableSelection}
				refreshSecuritiesList={() => refetchHoldingsList && refetchHoldingsList()}
				debtHoldings={debtHoldings}
			/>

			<BulkDeleteHoldingEventDrawer
				eventIds={bulkDeleteEventIds}
				opened={showBulkDeleteDrawer}
				onClose={() => {
					setShowBulkDeleteDrawer(false);
					setBulkDeleteEventIds([]);
				}}
				onSuccess={resetTableSelection}
				refreshSecuritiesList={() => refetchHoldingsList && refetchHoldingsList()}
			/>
		</>
	);
};
